import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { apiGet } from '../utils/api'

export type Tenant = { id: number; name: string; active: boolean }

type Ctx = {
  tenants: Tenant[]
  current?: Tenant
  setCurrentById: (id: number) => void
  refresh: () => Promise<void>
}

const KEY = 'currentTenantId'
const TenantCtx = createContext<Ctx | null>(null)
const DefaultTenantCtx: Ctx = {
  tenants: [],
  current: undefined,
  setCurrentById: () => {},
  refresh: async () => {}
}

export function useTenant() {
  try {
    const ctx = useContext(TenantCtx)
    return ctx ?? DefaultTenantCtx
  } catch {
    return DefaultTenantCtx
  }
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [currentId, setCurrentId] = useState<number | undefined>(() => {
    const saved = localStorage.getItem(KEY)
    return saved ? Number(saved) : undefined
  })

  const refresh = async () => {
    const t: Tenant[] = await apiGet('/tenants')
    setTenants(t)
    if (!currentId && t.length) {
      const nonDefault = t.find(x => (x.name || '').toLowerCase().trim() !== 'default')
      const pick = nonDefault || t[0]
      setCurrentId(pick.id)
      localStorage.setItem(KEY, String(pick.id))
    }
  }

  useEffect(() => { refresh() }, [])

  const setCurrentById = (id: number) => {
    setCurrentId(id)
    localStorage.setItem(KEY, String(id))
  }

  const current = useMemo(() => tenants.find(x => x.id === currentId), [tenants, currentId])

  return (
    <TenantCtx.Provider value={{ tenants, current, setCurrentById, refresh }}>
      {children}
    </TenantCtx.Provider>
  )
}
